import React, { useState, useEffect, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Box, useMediaQuery, Typography, Button } from "@mui/material";
import { Download } from "@mui/icons-material";
import { doc, getDoc, setDoc, updateDoc, onSnapshot } from "firebase/firestore";
import { db, functions } from "../firebaseConfig";
import { useAtom } from "jotai";
import { themeAtom, labelsAtom } from "../-Atoms";
import {
  ResetSelectsDialog,
  ConfirmSelectsDialog,
} from "../ClientSelects/DialogComponents";
import PortalHeader from "./components/PortalHeader";
import FolderCollection, { CollapsibleParentFolder } from "./components/FolderCollection";
// import ClientSelectsContainer from "./components/ClientSelectsContainer";
import LoadingState from "./components/LoadingState";
import ErrorState from "./components/ErrorState";
import ImageGrid from "../ClientSelects/ImageGrid";
import useJobData from "./hooks/useJobData";
import useDropboxFolders from "./hooks/useDropboxFolders";
import { confirmClientSelects } from "../ClientSelects/selectsHelpers";

const SupplyPortal = ({ jobID }) => {
  const [globalTheme] = useAtom(themeAtom);
  const [labels] = useAtom(labelsAtom);
  const [editMode, setEditMode] = useState(false);
  const [loadedFolders, setLoadedFolders] = useState(new Set());
  const [selectedImages, setSelectedImages] = useState([]);
  const isSmallScreen = useMediaQuery("(max-width:1200px)");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [confirmSelectsDialogOpen, setConfirmSelectsDialogOpen] =
    useState(false);
  const [selectsMadeMap, setSelectsMadeMap] = useState({});
  const [selectsSetupMap, setSelectsSetupMap] = useState({});
  const [selectedCountMap, setSelectedCountMap] = useState({});
  const [editingJobId, setEditingJobId] = useState(null);
  const [resetTrigger, setResetTrigger] = useState(0);
  const [folderFileCounts, setFolderFileCounts] = useState({});
  const [isConfirmingSelects, setIsConfirmingSelects] = useState(false);

  const localStorageKey = "gridColumnsAcross";
  const storedColumns = parseInt(localStorage.getItem(localStorageKey), 10);
  const [gridColumns, setGridColumns] = useState(
    storedColumns && storedColumns >= 1 && storedColumns <= 5
      ? storedColumns
      : 3
  );

  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const shouldEdit = searchParams.get("edit") === "true";

  const [dropboxPath, setDropboxPath] = useState("");
  const [jobName, setJobName] = useState("");
  const [jobDoc, setJobDoc] = useState("");

  const { jobData, allJobData, loading: jobLoading, error: jobError } = useJobData(jobID);
  const {
    folders,
    error: foldersError,
  } = useDropboxFolders(allJobData, jobID);

  useEffect(() => {
    if (jobData) {
      const jn = `${jobData.jobNumber}-${jobData.client}${jobData.jobTypeShort}`;
      setJobName(jn);
      setJobDoc(jobData);
      let dbPath = `/${jobData.client}/${jn}`;
      if (jobData.multipleDays) {
        dbPath = `/${jobData.client}/${jn}/${jobData.multipleDays}`;
      }
      setDropboxPath(dbPath);
    }
  }, [jobData]);

  useEffect(() => {
    if (allJobData && allJobData.length > 0) {
      const unsubs = [];
      allJobData.forEach((job) => {
        if (!job.id) {
          console.warn("Job missing ID in SupplyPortal subscription loop:", job);
          return;
        }
        const unsub = onSnapshot(doc(db, "ClientSelects", job.id), (doc) => {
          try {
            const exists = doc.exists();
            setSelectsSetupMap((prev) => ({ ...prev, [job.id]: exists }));
            if (exists) {
              const data = doc.data();
              setSelectsMadeMap((prev) => ({
                ...prev,
                [job.id]: data.selectsMade || false,
              }));
              setSelectedCountMap((prev) => ({
                ...prev,
                [job.id]: data.selectedImages?.length || 0,
              }));
            }
          } catch (e) {
            console.error(e);
          }
        });
        unsubs.push(unsub);
      });

      return () => {
        unsubs.forEach((unsub) => unsub());
      };
    } else if (jobID) {
      // Fallback for single job if allJobData not yet populated or single job
      let unsub = onSnapshot(doc(db, "ClientSelects", jobID), (doc) => {
        try {
          const exists = doc.exists();
          setSelectsSetupMap((prev) => ({ ...prev, [jobID]: exists }));
          if (exists) {
            const data = doc.data();
            setSelectsMadeMap((prev) => ({
              ...prev,
              [jobID]: data.selectsMade || false,
            }));
            setSelectedCountMap((prev) => ({
              ...prev,
              [jobID]: data.selectedImages?.length || 0,
            }));
          }
        } catch (e) {
          console.error(e);
        }
      });

      return () => {
        unsub();
      };
    }
  }, [allJobData, jobID]);

  useEffect(() => {
    if (shouldEdit) {
      setEditMode(true);
    }
  }, [shouldEdit]);

  // Derived state for current context
  const currentJobId = editingJobId || jobID;
  const selectsMade = selectsMadeMap[currentJobId] || false;
  const selectsSetup = selectsSetupMap[currentJobId] || false;

  const hasFolderResult = folders.length > 0 || !!foldersError;
  const waitingForFolders = Boolean(jobData) && !hasFolderResult;
  const shouldShowLoadingState = jobLoading || waitingForFolders;
  const error = jobError || foldersError;

  // Check for "supplied" label
  const suppliedLabelId = labels.find((l) => l.name.toLowerCase() === "supplied")?.id;
  const hasSuppliedLabel =
    jobData &&
    suppliedLabelId &&
    ((jobData.idLabels && jobData.idLabels.includes(suppliedLabelId)) ||
      (jobData.idLabelsPermanent && jobData.idLabelsPermanent.includes(suppliedLabelId)));

  // Folders are already in the correct order from useDropboxFolders
  const sortedFolders = folders;

  // console.log(...folders);

  const handleEditSelects = async (targetJobId) => {
    const idToUse = targetJobId || jobID;

    // Navigate to the target job's URL with edit param
    // This ensures the title and context are correct
    history.push(`/supply/${idToUse}?edit=true`);
  };

  const handleBackToStoryboards = () => {
    setEditMode(false);
    setEditingJobId(null);
    // Remove edit param from URL
    history.push(`/supply/${jobID}`);
  };

  useEffect(() => {
    localStorage.setItem(localStorageKey, gridColumns);
  }, [gridColumns]);

  const handleOpenDialog = () => {
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const handleReset = async () => {
    if (selectsMade) return;

    setSelectedImages([]);
    setResetTrigger((prev) => prev + 1);

    try {
      await updateDoc(doc(db, "ClientSelects", currentJobId), {
        selectedImages: [],
      });
    } catch (error) {
      console.error("Error resetting selects in database:", error);
    }

    handleCloseDialog();
  };

  const handleConfirmSelects = async () => {
    try {
      const { result } = await confirmClientSelects({
        db,
        functions,
        jobID: currentJobId,
        selectedImages,
        dropboxPath,
        jobDoc,
        jobName,
      });

      if (result.data?.success) {
        // State update handled by listener
      } else {
        console.error("Error starting copy process:", result.data.error);
      }
    } catch (error) {
      console.error("Error in confirm selects:", error);
    }
  };

  const handleFolderLoadingComplete = useCallback(
    (folderPath, fileCount = 0) => {
      // console.log(`📋 SupplyPortal: ${folderPath} completed loading with ${fileCount} files`);
      setLoadedFolders((prev) => new Set([...prev, folderPath]));
      setFolderFileCounts((prev) => ({ ...prev, [folderPath]: fileCount }));
    },
    []
  );

  // Prevent window closing during confirmation process
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isConfirmingSelects) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isConfirmingSelects]);

  if (shouldShowLoadingState) {
    return <LoadingState />;
  }

  if (!jobData) {
    return <ErrorState error="Job not found" themeMode={globalTheme} />;
  }

  if (error) {
    return <ErrorState error={error} themeMode={globalTheme} />;
  }

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: globalTheme === "dark" ? "#141517" : "#f1f2f4",
        color: globalTheme === "dark" ? "#e8eaed" : "#111315",
        overflow: "hidden",
      }}
    >
      {/* Fixed Header */}
      <PortalHeader
        jobData={jobData}
        editMode={editMode}
        onBackToStoryboards={handleBackToStoryboards}
        selectedCount={selectedImages.length}
        selectsMade={selectsMade}
        onResetSelects={handleOpenDialog}
        onConfirmSelects={() => setConfirmSelectsDialogOpen(true)}
        gridColumns={gridColumns}
        setGridColumns={setGridColumns}
      />

      {/* Scrollable Content Area */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          backgroundColor: "transparent",
        }}
      >
        <Box
          sx={{
            mt: editMode ? 0 : 2,
            pb: editMode ? 0 : 3,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: editMode ? "100%" : "auto",
          }}
        >
          {!editMode ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: 1,
                width: "95%",
                maxWidth: "1600px",
                height: "auto",
                px: 2,
              }}
            >
              {/* Render folders grouped by day */}
              {sortedFolders.map((dayGroup, dayIndex) => {
                const {
                  dayLabel,
                  folders: dayFolders,
                  isMultiDay,
                  isSupplyGroup,
                  jobId: dayJobId,
                } = dayGroup;
                const showHeader = isSupplyGroup || (isMultiDay && dayLabel);
                const daySelectsSetup = selectsSetupMap[dayJobId] || false;
                const daySelectsMade = selectsMadeMap[dayJobId] || false;
                const daySelectedCount = selectedCountMap[dayJobId] || 0;

                return (
                  <React.Fragment key={dayGroup.jobId}>
                    {/* Day/Section Header */}
                    {showHeader && (
                      <Box
                        sx={{
                          width: "100%",
                          pb: 1,
                          mb: 2,
                          mt: dayIndex > 0 ? 4 : 0,
                          borderBottom: `1px solid ${globalTheme === "dark" ? "#333" : "#e0e0e0"
                            }`,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              color: globalTheme === "dark" ? "#e8eaed" : "#111315",
                              fontSize: "1.1rem",
                            }}
                          >
                            {dayLabel}
                          </Typography>

                          {/* Show buttons for Supply group if available and not using collapsible parent */}
                          {isSupplyGroup && hasSuppliedLabel && dayGroup.rootSupplyLink && !dayFolders.some(f => f.isCollapsibleParent) && (
                            <Box sx={{ display: "flex", gap: 2, ml: "auto" }}>
                              <Button
                                variant="contained"
                                component="a"
                                href={dayGroup.rootSupplyLink.replace("?dl=0", "?dl=1")}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  textTransform: "none",
                                  fontWeight: 600,
                                  borderRadius: 1,
                                  px: 2,
                                  py: 0.65,
                                  fontSize: isSmallScreen ? "0.75rem" : "0.85rem",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.75,
                                  minHeight: "34px",
                                  boxShadow: "none",
                                  backgroundColor: "#66bb6a",
                                  color: "#fff",
                                  "&:hover": {
                                    backgroundColor: "#4caf50",
                                  },
                                }}
                              >
                                <Download fontSize="small" />
                                {isSmallScreen ? "" : "Download"}
                              </Button>
                              <Button
                                variant="contained"
                                component="a"
                                href={dayGroup.rootSupplyLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  textTransform: "none",
                                  fontWeight: 600,
                                  borderRadius: 1,
                                  px: 2,
                                  py: 0.65,
                                  fontSize: isSmallScreen ? "0.75rem" : "0.85rem",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.75,
                                  minHeight: "34px",
                                  boxShadow: "none",
                                  backgroundColor: "#0061FE",
                                  color: "#fff",
                                  "&:hover": {
                                    backgroundColor: "#0052d4",
                                  },
                                }}
                              >
                                <img
                                  src="/Dropbox_Icon.png"
                                  alt="Dropbox"
                                  style={{ height: 18, width: "auto", objectFit: "contain" }}
                                />
                                {isSmallScreen ? "" : "Dropbox"}
                              </Button>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}

                    {/* Render folders for this day */}
                    {dayFolders.map((folder, folderIndex) => {
                      const isActive = true;
                      const isCompleted = loadedFolders.has(folder.path);

                      // Handle Collapsible Parent Folders (>4 subfolders)
                      if (folder.isCollapsibleParent) {
                        return (
                          <CollapsibleParentFolder
                            key={folder.path}
                            folder={folder}
                            onLoadingComplete={handleFolderLoadingComplete}
                            selectsMade={daySelectsMade}
                            selectedCount={daySelectedCount}
                            onEditSelects={() => handleEditSelects(dayJobId)}
                            selectsSetup={daySelectsSetup}
                          />
                        );
                      }

                      // Handle Supply subfolders
                      if (folder.isSupplySubfolder) {
                        // Check if we need a separator (change in parent folder)
                        const prevFolder =
                          folderIndex > 0 ? dayFolders[folderIndex - 1] : null;
                        const showSeparator =
                          prevFolder &&
                          folder.parentFolder &&
                          prevFolder.parentFolder &&
                          folder.parentFolder !== prevFolder.parentFolder;

                        return (
                          <React.Fragment key={folder.path}>
                            {showSeparator && (
                              <Box
                                sx={{
                                  width: "100%",
                                  height: "1px",
                                  backgroundColor:
                                    globalTheme === "dark" ? "#333" : "#e0e0e0",
                                  my: 2,
                                }}
                              />
                            )}
                            <Box
                              sx={{
                                width: "100%",
                              }}
                            >
                              <FolderCollection
                                folder={folder}
                                isActive={isActive}
                                isCompleted={isCompleted}
                                onLoadingComplete={handleFolderLoadingComplete}
                                compact={true}
                                selectsMade={daySelectsMade}
                                selectedCount={daySelectedCount}
                                onEditSelects={() => handleEditSelects(dayJobId)}
                                selectsSetup={daySelectsSetup}
                              />
                            </Box>
                          </React.Fragment>
                        );
                      }

                      // Handle Content folder as compact
                      if (folder.name === "Content") {
                        return (
                          <Box
                            key={folder.path}
                            sx={{
                              width: "100%",
                            }}
                          >
                            <FolderCollection
                              folder={folder}
                              isActive={isActive}
                              isCompleted={isCompleted}
                              onLoadingComplete={handleFolderLoadingComplete}
                              compact={true}
                              selectsMade={daySelectsMade}
                              selectedCount={daySelectedCount}
                              onEditSelects={() => handleEditSelects(dayJobId)}
                              selectsSetup={daySelectsSetup}
                            />
                          </Box>
                        );
                      }

                      if (folder.name === "Early Supply") {
                        return (
                          <Box
                            key={folder.path}
                            sx={{
                              width: "100%",
                            }}
                          >
                            <FolderCollection
                              folder={folder}
                              isActive={isActive}
                              isCompleted={isCompleted}
                              onLoadingComplete={handleFolderLoadingComplete}
                              compact={true}
                              selectsMade={daySelectsMade}
                              selectedCount={daySelectedCount}
                              onEditSelects={() => handleEditSelects(dayJobId)}
                              selectsSetup={daySelectsSetup}
                            />
                          </Box>
                        );
                      }

                      // Handle custom/other folders (before Storyboards)
                      // These are folders not matching any predefined type
                      const predefinedFolders = ["Content", "Early Supply", "Storyboards", "Storyboard Selects", "Quickproofs"];
                      if (!predefinedFolders.includes(folder.name) && !folder.isSupplySubfolder && !folder.isCollapsibleParent) {
                        return (
                          <Box
                            key={folder.path}
                            sx={{
                              width: "100%",
                            }}
                          >
                            <FolderCollection
                              folder={folder}
                              isActive={isActive}
                              isCompleted={isCompleted}
                              onLoadingComplete={handleFolderLoadingComplete}
                              compact={folder.compact || true}
                              selectsMade={daySelectsMade}
                              selectedCount={daySelectedCount}
                              onEditSelects={() => handleEditSelects(dayJobId)}
                              selectsSetup={daySelectsSetup}
                            />
                          </Box>
                        );
                      }

                      if (folder.name === "Storyboards") {
                        const hasContentOrSupply = dayFolders.some(
                          (f) => f.name === "Content" || f.isSupplySubfolder
                        );

                        const storyboardSelectsFolder = dayFolders.find(
                          (f) => f.name === "Storyboard Selects"
                        );
                        const storyboardSelectsCount = storyboardSelectsFolder
                          ? folderFileCounts[storyboardSelectsFolder.path]
                          : 0;
                        const displayCount =
                          daySelectedCount > 0
                            ? daySelectedCount
                            : storyboardSelectsCount || 0;

                        return (
                          <React.Fragment key={folder.path}>
                            {/* Separator - only show if there's content above */}
                            {hasContentOrSupply && (
                              <Box
                                sx={{
                                  width: "100%",
                                  height: "1px",
                                  backgroundColor:
                                    globalTheme === "dark" ? "#333" : "#e0e0e0",
                                  my: 2,
                                }}
                              />
                            )}

                            {/* Storyboards folder */}
                            <Box
                              sx={{
                                width: "100%",
                                minHeight: "200px",
                                height: "auto",
                              }}
                            >
                              <FolderCollection
                                folder={folder}
                                isActive={isActive}
                                isCompleted={loadedFolders.has(folder.path)}
                                onLoadingComplete={handleFolderLoadingComplete}
                                selectsMade={daySelectsMade}
                                selectedCount={displayCount}
                                onEditSelects={() => handleEditSelects(dayJobId)}
                                selectsSetup={daySelectsSetup}
                              />
                            </Box>
                          </React.Fragment>
                        );
                      }

                      // Handle Storyboard Selects and Quickproofs together in a row
                      if (folder.name === "Storyboard Selects") {
                        const quickproofsFolder = dayFolders.find(
                          (f) => f.name === "Quickproofs"
                        );

                        // Check if Quickproofs will actually be visible (exists and has files > 0)
                        const quickproofsFileCount =
                          quickproofsFolder && quickproofsFolder.path
                            ? folderFileCounts[quickproofsFolder.path] ?? null
                            : null;
                        const quickproofsWillBeVisible =
                          quickproofsFolder &&
                          quickproofsFolder.exists &&
                          (quickproofsFileCount === null ||
                            quickproofsFileCount > 0);

                        return (
                          <Box
                            key="selects-quickproofs-container"
                            sx={{
                              width: "100%",
                              display: "flex",
                              flexDirection: isSmallScreen ? "column" : "row",
                              gap: 1,
                              alignItems: "stretch",
                              height: "auto",
                              mt: 2,
                            }}
                          >
                            {/* Storyboard Selects */}
                            <Box
                              sx={{
                                flex: quickproofsWillBeVisible ? 1 : "1 1 100%",
                              }}
                            >
                              <FolderCollection
                                folder={folder}
                                isActive={isActive}
                                isCompleted={loadedFolders.has(folder.path)}
                                onLoadingComplete={handleFolderLoadingComplete}
                                compact={true}
                                selectsMade={daySelectsMade}
                                selectedCount={daySelectedCount}
                                onEditSelects={() => handleEditSelects(dayJobId)}
                                selectsSetup={daySelectsSetup}
                              />
                            </Box>

                            {/* Quickproofs */}
                            {quickproofsWillBeVisible && (
                              <Box sx={{ flex: 1 }}>
                                <FolderCollection
                                  folder={quickproofsFolder}
                                  isActive={isActive}
                                  isCompleted={loadedFolders.has(
                                    quickproofsFolder.path
                                  )}
                                  onLoadingComplete={
                                    handleFolderLoadingComplete
                                  }
                                  compact={true}
                                  selectsMade={daySelectsMade}
                                  selectedCount={daySelectedCount}
                                  onEditSelects={() => handleEditSelects(dayJobId)}
                                  selectsSetup={daySelectsSetup}
                                />
                              </Box>
                            )}
                          </Box>
                        );
                      }

                      // Skip Quickproofs since it's handled with Storyboard Selects
                      if (folder.name === "Quickproofs") {
                        return null;
                      }

                      // Fallback - should not be reached
                      return null;
                    })}
                  </React.Fragment>
                );
              })}
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                height: "100%",
                width: "100%",
                overflow: "hidden",
              }}
            >
              <ImageGrid
                jobID={currentJobId}
                embedded={true}
                onSelectedImagesChange={setSelectedImages}
                gridColumns={gridColumns}
                resetTrigger={resetTrigger}
                selectsMade={selectsMade}
              />
            </Box>
          )}
        </Box>

        {/* Dialog Components */}
        <ResetSelectsDialog
          open={isDialogOpen}
          onClose={handleCloseDialog}
          onReset={handleReset}
        />

        <ConfirmSelectsDialog
          open={confirmSelectsDialogOpen}
          onClose={() => setConfirmSelectsDialogOpen(false)}
          onConfirm={handleConfirmSelects}
          selectedCount={selectedImages.length}
          onLoadingChange={setIsConfirmingSelects}
        />
      </Box>
    </Box >
  );
};

export default SupplyPortal;
